import { RestService } from "@sinossi/mates-react-library";
import {
	Action,
	AssignableUsers, AssignRequest, CreateSpecificWorkflow,
	NewStepRequest,
	NewWorkflowAction,
	NewWorkflowTemplate,
	PaginatedResponse,
	SearchFilter,
	Template, UpdateActionRequest,
	Workflow, WorkflowObjectType, WorkflowTemplateSteps
} from "models";

export class WorkflowService {
	static getWorkflowTemplatesList = (request: SearchFilter, callback: any, catchCallback: any) => {
		RestService
			.post<PaginatedResponse<Template>>(`/api/workflow-templates/filter`, request)
			.then(callback)
			.catch(catchCallback)
	}

	static getTemplateStepsList = (workflowId: string, callback: any, catchCallback: any) => {
		RestService
			.get<WorkflowTemplateSteps>(`/api/workflow-template/${workflowId}/steps`)
			.then(callback)
			.catch(catchCallback)
	}

	static createWorkflowFromTemplate = (templateId: string, request: { match: string | null, asset: string | null }, callback: any, catchCallback: any) => {
		RestService
			.post<any>(`/api/workflow-template/${templateId}/init`, request)
			.then(callback)
			.catch(catchCallback)
	}

	static setWorkflowTemplateDefault = (templateId: string, callback: any, catchCallback: any) => {
		RestService
			.post<any>(`/api/workflow-template/${templateId}/default`, templateId)
			.then(callback)
			.catch(catchCallback)
	}

	static cloneWorkflowTemplate = (templateId: string, callback: any, catchCallback: any) => {
		RestService
			.post<any>(`/api/workflow-template/${templateId}/clone`, templateId)
			.then(callback)
			.catch(catchCallback)
	}

	static deleteWorkflowTemplate = (templateId: string, callback: any, catchCallback: any) => {
		RestService
			.delete<any>(`/api/workflow-template/${templateId}`)
			.then(callback)
			.catch(catchCallback)
	}

	static createNewWorkflowTemplate = (request: NewWorkflowTemplate, callback: any, catchCallback: any) => {
		RestService
			.post<any>(`/api/workflow-template`, request)
			.then(callback)
			.catch(catchCallback)
	}

	static createNewStep = (request: NewStepRequest, templateId: string, callback: any, catchCallback: any) => {
		RestService
			.post<any>(`api/workflow-template/${templateId}/step`, request)
			.then(callback)
			.catch(catchCallback)
	}

	static updateStep = (request: NewStepRequest, stepId: string, callback: any, catchCallback: any) => {
		RestService
			.put<any>(`api/workflow-step/${stepId}`, request)
			.then(callback)
			.catch(catchCallback)
	}

	static deleteStep = (stepId: string, callback: any, catchCallback: any) => {
		RestService
			.delete<any>(`api/workflow-step/${stepId}`)
			.then(callback)
			.catch(catchCallback)
	}

	static cloneStep = (stepId: string, callback: any, catchCallback: any) => {
		RestService
			.post<any>(`api/workflow-step/${stepId}/clone`, stepId)
			.then(callback)
			.catch(catchCallback)
	}

	static getWorkflows = (request: SearchFilter, callback: any, catchCallback: any) => {
		RestService
			.post<PaginatedResponse<Workflow>>(`/api/workflows/filter`, request)
			.then(callback)
			.catch(catchCallback)
	}

	static deleteWorkflow = (workflowId: string, callback: any, catchCallback: any) => {
		RestService
			.delete<any>(`api/workflow/${workflowId}`)
			.then(callback)
			.catch(catchCallback)
	}

	static getWorkflowActionList = (workflowId: string, callback: any, catchCallback: any) => {
		RestService
			.get<WorkflowTemplateSteps>(`/api/workflow/${workflowId}/actions`)
			.then(callback)
			.catch(catchCallback)
	}

	static createNewAction = (request: NewWorkflowAction, workflowId: string, callback: any, catchCallback: any) => {
		RestService
			.post<any>(`api/workflow/${workflowId}/action`, request)
			.then(callback)
			.catch(catchCallback)
	}

	static doneAction = (actionId: string, callback: any, catchCallback: any) => {
		RestService
			.post<any>(`api/action/${actionId}/done`, actionId)
			.then(callback)
			.catch(catchCallback)
	}

	static deferAction = (actionId: string, request: { deadline: string }, callback: any, catchCallback: any) => {
		RestService
			.post<any>(`api/action/${actionId}/defer`, request)
			.then(callback)
			.catch(catchCallback)
	}

	static markAsDone = (actionId: string, callback: any, catchCallback: any) => {
		RestService
			.post(`api/action/${actionId}/done`, {})
			.then(callback)
			.catch(catchCallback)
	}

	static skipAction = (actionId: string, callback: any, catchCallback: any) => {
		RestService
			.post<any>(`api/action/${actionId}/skip`, {})
			.then(callback)
			.catch(catchCallback)
	}

	static updateActionContent = (actionId: string, request: UpdateActionRequest, callback: any, catchCallback: any) => {
		RestService
			.put<Action>(`api/action/${actionId}`, request)
			.then(callback)
			.catch(catchCallback)
	}

	static getAssignableUsers = (actionId: string, callback: any, catchCallback: any) => {
		RestService
			.get<AssignableUsers[]>(`/api/workflows/action/${actionId}/assignable-users`)
			.then(callback)
			.catch(catchCallback)
	}

	static getAssignableUsersWithSearch = (actionId: string, search: string, accountType: string | null, callback: any, catchCallback: any) => {
		let url = `/api/workflows/action/${actionId}/assignable-users?searchString=${search}`;
		if (accountType) {
			url += `&accountType=${accountType}`;
		}
		
		RestService
			.get<AssignableUsers[]>(url)
			.then(callback)
			.catch(catchCallback)
	}


	static assignTemplate = (templateId: string, request: AssignRequest, callback: any, catchCallback: any) => {
		RestService
			.post(`/api/workflow-template/${templateId}/assign`, request)
			.then(callback)
			.catch(callback)
	}

	static assignWorkflow = (workflowId: string, request: AssignRequest, callback: any, catchCallback: any) => {
		RestService
			.post(`/api/workflow/${workflowId}/assign`, request)
			.then(callback)
			.catch(catchCallback)
	}

	static assignWorkflowAction = (actionId: string, request: AssignRequest, callback: any, catchCallback: any) => {
		RestService
			.post(`/api/action/${actionId}/assign`, request)
			.then(callback)
			.catch(catchCallback)
	}

	static getTemplateListByType = (templateType: WorkflowObjectType, callback: any, catchCallback: any) => {
		RestService
			.get(`/api/workflow-templates/${templateType}`)
			.then(callback)
			.catch(catchCallback)
	}

	static createWorkflowFromSpecificObject = (request: CreateSpecificWorkflow, callback: any, catchCallback: any) => {
		RestService
			.post(`/api/workflows/init`, request)
			.then(callback)
			.catch(catchCallback)
	}
}
